import { useEffect, useRef, useState, type ReactElement } from 'react';
import { apiFetch, ApiError, useSession, type MeResponse, type Profile, type Relationship } from '../../auth';
import { track, trackFailed } from '../../../../../shared-web/track';
import { apiFailureReason } from '../api';
import { buildSmsHref, formatPhone } from '../../../../../shared-web/onboarding-phone';
import { isInAppWebview } from '../../../../../shared-web/ua-detect';
import { fullDate } from '../format';
import { toast } from '../../../design-system/components';
import { resolveBack, sanitizeReturnTo } from '../router';
import { useRequireAuth } from '../use-require-auth';
import { PageBack } from '../components/PageBack';
import { PageHome } from '../components/PageHome';
import { DeleteAccountModal } from '../components/DeleteAccountModal';
import { Modal } from '../components/Modal';
import { ControlRow } from '../components/ControlRow';
import { Switch } from '../components/Switch';
import { HelpTip } from '../components/HelpTip';
import { PrivacyOffSheet, type PrivacyOffKey } from '../components/PrivacyOffSheet';
import { CLIENT_COPY } from '../../../../../shared/client-copy.js';
import { GOOGLE_CLIENT_ID, consumeGoogleLanding, googleLinkHref } from '../../../../../shared-web/google-signin';
import { GoogleG } from '../../../../../shared-web/google-button';
import { clearPhoneHint, markPhoneHintSeen } from '../../../../../shared-web/phone-hint';
import './Settings.css';

/** GET /api/app/auth/identities —— 脱敏手机号 + 已绑第三方(App Settings 同一张脸)。 */
interface Identities {
  phone?: string | null;
  /** 邮箱形态的 iMessage handle(用邮箱当 iMessage 注册的账号的根身份),已脱敏。 */
  imessage?: { handle?: string | null } | null;
  google?: { email?: string | null } | null;
  apple?: { email?: string | null } | null;
}

type PrivacyKey = 'share' | 'background_experience' | 'model_training' | 'product_improvement';
type PrivacyPrefs = Record<PrivacyKey, boolean>;

interface StoryLanguageResponse {
  ok: true;
  /** The language the next voice telling will be spoken in. */
  language: string;
  /** The offered languages, each labelled with its own endonym. */
  languages: Array<{ code: string; label: string }>;
}

type SmsNotifications = NonNullable<MeResponse['notifications']>;

type ProfileKey = 'pronouns' | 'birth_year' | 'occupation' | 'relationship' | 'location';
const NO_PROFILE: Profile = { pronouns: null, birth_year: null, occupation: null, relationship: null, location: null, declined: [] };
const PRONOUN_LABELS: Record<NonNullable<Profile['pronouns']>, string> = { she: 'She', he: 'He', they: 'They' };
const RELATIONSHIP_LABELS: Record<Relationship, string> = {
  single: 'Single',
  in_a_relationship: 'In a relationship',
  married: 'Married',
  separated: 'Separated',
  divorced: 'Divorced',
  widowed: 'Widowed',
};
/** A row answered "Prefer not to say" (2026-10-20): kept as that, so Ember leaves it empty (ember/about-you.js DECLINED). */
const DECLINED = 'prefer_not_to_say';
type ProfileValue = string | number | null;
/** ui.clicked per row once its change is saved (registry: state = set | cleared | declined, never the value). */
const PROFILE_LOG: Record<ProfileKey, string> = {
  pronouns: 'settings_profile_pronouns',
  birth_year: 'settings_profile_birth_year',
  occupation: 'settings_profile_occupation',
  relationship: 'settings_profile_relationship',
  location: 'settings_profile_location',
};
/** What you do: one line, the server's cap (ember/about-you.js PROFILE_OCCUPATION_MAX). */
const OCCUPATION_MAX = 60;

/** The years the Year of birth picker offers, newest first: 18 to 110 this year (PUT /api/me/profile's bound). */
function birthYears(): number[] {
  const year = new Date().getFullYear();
  return Array.from({ length: 110 - 18 + 1 }, (_, i) => year - 18 - i);
}

/** How long Settings keeps asking /api/me after handing the person to Messages. */
const NOTIFY_POLL_MS = 4000;
const NOTIFY_POLL_LIMIT_MS = 3 * 60 * 1000;

/**
 * The two data-use switches, both on by default. Server truth is
 * actors.metadata.privacy_prefs, read via GET /api/me and written via
 * PUT /api/me/privacy. `help` is what the ? opens (2026-09-12): plain words,
 * one source for all three clients since 2026-10-21 (src/shared/client-copy.js
 * legal.data_controls, served to the apps over /api/app/config).
 */
const DATA_CONTROLS: Array<{ key: PrivacyKey; title: string; detail: string; help: readonly string[] }> = [
  {
    key: 'model_training',
    title: 'Help improve Ember’s models',
    detail: 'Allow content you share with Ember to be used to improve the models.',
    help: CLIENT_COPY.legal.data_controls.model_training.help,
  },
  {
    key: 'product_improvement',
    title: 'Help improve the product',
    detail: 'Allow usage data to help us understand and improve the Ember experience.',
    help: CLIENT_COPY.legal.data_controls.product_improvement.help,
  },
];

/** Settings › Help Ember understand similar situations: the lawyer's words, served to the apps too (2026-10-21 Riley). */
const BACKGROUND_COPY = CLIENT_COPY.legal.background;
/** Settings › Another person reading your Ember: the same one source (2026-10-21 Riley). */
const SHARE_COPY = CLIENT_COPY.legal.share;

/** The two account-wide switches whose turning off asks a word first (PrivacyOffSheet). */
const ASKS_FIRST: Partial<Record<PrivacyKey, PrivacyOffKey>> = {
  share: 'share',
  background_experience: 'background',
};

/**
 * Settings (/settings) — reached from the account menu. The spoken language of
 * voice tellings, notifications, data controls, and delete account, which
 * moved here from the account menu itself (2026-08-23); the confirm modal and
 * the soft-delete flow behind it are unchanged. The owner's symbol + line
 * moved home to the /me header (2026-09-01, retiring /settings/space).
 */
export function SettingsPage(): ReactElement {
  const status = useRequireAuth('/settings');
  // The session carries the Background experience state for the surfaces that
  // show it (Result foot, owner's Ember page); a flip here refreshes it.
  const { refresh: refreshSession } = useSession();
  const [prefs, setPrefs] = useState<PrivacyPrefs | null>(null);
  const [saving, setSaving] = useState<PrivacyKey | null>(null);
  // The word before an account-wide switch goes off (2026-09-12): which one is asking.
  const [asking, setAsking] = useState<PrivacyKey | null>(null);
  // null until the language read answers — the row renders disabled rather
  // than showing a default that may flip a beat later. The offered set comes
  // with it: the languages the voice shell has voices for is an edge fact, not
  // a copy of one kept here.
  const [language, setLanguage] = useState<StoryLanguageResponse | null>(null);
  const [languageSaving, setLanguageSaving] = useState(false);
  // About you (2026-10-20 Riley): null until /api/me answers — the rows render disabled meanwhile, like the
  // language row. `profileSaving` names the row whose PUT is in flight.
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileSaving, setProfileSaving] = useState<ProfileKey | null>(null);
  const [occupationOpen, setOccupationOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // The "Text messages" switch. null until /api/me answers. `pending` is the
  // stretch between handing the person to Messages with the consent text
  // drafted and the server seeing it arrive — the switch turns on only then.
  const [notify, setNotify] = useState<SmsNotifications | null>(null);
  const [notifyPending, setNotifyPending] = useState(false);
  // The text handed to Messages, said on the page too (2026-10-30 e2e: on a desktop Messages never opens, and the row sat
  // on Waiting… with nothing to send).
  const [notifyBody, setNotifyBody] = useState('');
  const [notifySaving, setNotifySaving] = useState(false);
  // STOP standing on the account: the row says so and names the way back (any
  // text to the line); the switch is held off, because no text from Ember can
  // go out until they do.
  const [optedOut, setOptedOut] = useState(false);
  // A phone just added here goes straight on to the consent text (2026-10-19 Riley): the number alone never turned
  // texts on, and people left Settings thinking it had.
  const [textsPromptOpen, setTextsPromptOpen] = useState(false);
  const notifyPollStartedAt = useRef(0);
  // One settings_load failure per visit, however often the effect re-runs.
  const loadFailedSent = useRef(false);
  // ── 绑定面(2026-09-09):Phone + Google 两行,App Settings 的网页同款。
  //    identities 读不到就不摆绑定行,页面其余照常——绑定面是加法。 ──
  const [identities, setIdentities] = useState<Identities | null>(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [phoneHint, setPhoneHint] = useState(false);
  // Google 在内置浏览器里必败(封禁 webview OAuth),不摆
  const googleRowReady = Boolean(GOOGLE_CLIENT_ID) && !isInAppWebview();
  // 整页去 Google 会丢掉 SPA 的足迹(history.state 不跨文档):出发时把此刻的
  // Back 落点塞进 return_to 的 ?back=,回来后交还 PageBack——否则 Back 只能落
  // 到结构父级 /me/embers,从首页进来的人被莫名带去 notebook(2026-09-10 走查)。
  const [backAfterGoogle] = useState(() => {
    try { return sanitizeReturnTo(new URLSearchParams(window.location.search).get('back'), ''); }
    catch { return ''; }
  });

  const loadIdentities = async (): Promise<Identities | null> => {
    try {
      const ids = await apiFetch<Identities>('/api/app/auth/identities');
      setIdentities(ids);
      return ids;
    } catch { return null; }
  };

  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    let alive = true;
    // useRequireAuth handles the 401 bounce; anything else leaves the control
    // it belongs to disabled rather than showing a state we can't stand
    // behind. Either read failing is the same fact — this page did not load —
    // recorded once, with which read refused and how (apiFailureReason): the
    // 2026-09-15/16 iOS Safari failures off the reach_notice text's link were
    // three bare `load_failed` rows and nothing on the server.
    const loadFailed = (route: string) => (err: unknown): void => {
      if (loadFailedSent.current) return;
      loadFailedSent.current = true;
      trackFailed('settings_load', apiFailureReason(err), { route });
    };
    apiFetch<MeResponse>('/api/me')
      .then((me) => {
        if (!alive) return;
        setPrefs({
          share: me.privacy?.share !== false,
          background_experience: me.privacy?.background_experience !== false,
          model_training: me.privacy?.model_training !== false,
          product_improvement: me.privacy?.product_improvement !== false,
        });
        if (me.notifications) setNotify(me.notifications);
        setOptedOut(Boolean(me.messaging_opt_out?.opted_out));
        setProfile(me.profile || NO_PROFILE);
      })
      .catch(loadFailed('/api/me'));
    // The languages come from the server with the standing choice: the set the
    // voice shell actually has voices for is an edge fact, not a copy of it
    // kept here.
    apiFetch<StoryLanguageResponse>('/api/me/story-language')
      .then((next) => { if (alive) setLanguage(next); })
      .catch(loadFailed('/api/me/story-language'));
    // Google link 的回跳落点(授权码流):hash 报结果,读一次即抹
    const landing = consumeGoogleLanding();
    if (landing === 'linked') toast.success('Google connected');
    else if (landing === 'conflict') toast.error('That Google account is already connected to a different Ember account.');
    // 绑定面:读不到不算 settings_load 失败(加法);顺便结算一次性红点——
    // 没绑号且点还挂着 → 本页亮一次并记「已见」(菜单的点就此熄灭),绑了 → 清除。
    void loadIdentities().then((ids) => {
      if (!alive || !ids) return;
      if (ids.phone) clearPhoneHint();
      else setPhoneHint(markPhoneHintSeen());
    });
    return () => { alive = false; };
  }, [status]);


  // While pending, ask /api/me until the NOTIFY text has landed (the switch
  // reads active) or the wait runs out. Paused while the tab is hidden — the
  // person is in Messages — and resumed the moment they come back.
  useEffect(() => {
    if (!notifyPending) return undefined;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async (): Promise<void> => {
      if (!alive) return;
      if (Date.now() - notifyPollStartedAt.current > NOTIFY_POLL_LIMIT_MS) {
        setNotifyPending(false);
        return;
      }
      if (document.visibilityState === 'visible') {
        try {
          const me = await apiFetch<MeResponse>('/api/me');
          if (!alive) return;
          setOptedOut(Boolean(me.messaging_opt_out?.opted_out));
          if (me.notifications) {
            setNotify(me.notifications);
            if (me.notifications.sms.active) {
              setNotifyPending(false);
              toast.success('Ember can reach out');
              return;
            }
            // The text came from a number on another account (2026-11-03, as the apps since 11-02):
            // no consent is coming, so stop waiting and say why instead of Waiting… for three minutes
            const conflictAt = Date.parse(me.notifications.sms.conflict_at ?? '');
            if (conflictAt >= notifyPollStartedAt.current - 30_000) {
              setNotifyPending(false);
              toast.error('That number is already on another Ember account.');
              return;
            }
          }
        } catch {
          // A failed poll is just a missed beat; the next one asks again.
        }
      }
      timer = setTimeout(() => { void tick(); }, NOTIFY_POLL_MS);
    };
    const onVisible = (): void => { if (document.visibilityState === 'visible') void tick(); };
    document.addEventListener('visibilitychange', onVisible);
    timer = setTimeout(() => { void tick(); }, NOTIFY_POLL_MS);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [notifyPending]);

  /**
   * The consent is the person's own text or call, never a tap. So turning texts
   * on without one inside the window writes nothing: mint a consent code and
   * hand the person to Messages with the full sentence drafted to their own
   * Ember line, and the switch appears when that text lands. The code names
   * this account to the inbound gate, which also binds the sending phone to it
   * when the two have never met. Since 2026-10-19 (Riley) this is its own
   * action (the "Send Ember a text" row, and the step after a phone is added), not a
   * switch that could be tapped and then fail with no line.
   */
  const startConsentText = async (): Promise<void> => {
    if (!notify?.line || notifySaving) return;
    setNotifySaving(true);
    try {
      const minted = await apiFetch<{ ok: true; code: string; sms_body: string }>(
        '/api/me/notifications/consent-code',
        { method: 'POST' },
      );
      notifyPollStartedAt.current = Date.now();
      setNotifyBody(minted.sms_body);
      setNotifyPending(true);
      window.location.href = buildSmsHref(notify.line, minted.sms_body);
    } catch {
      toast.error("Couldn't get things ready. Try again.");
      trackFailed('settings_notify', 'consent_code_failed');
    } finally {
      setNotifySaving(false);
    }
  };

  /** The switch, shown only with the consent in hand: a plain write, in either direction. */
  const flipNotify = async (): Promise<void> => {
    if (!notify || notifySaving) return;
    const turningOn = !notify.sms.active;
    if (turningOn && !notify.sms.in_window) { void startConsentText(); return; }
    setNotifySaving(true);
    const previous = notify;
    setNotify({ ...notify, sms: { ...notify.sms, opted_out: !turningOn, active: turningOn } });
    try {
      const result = await apiFetch<{ ok: true; notifications: SmsNotifications }>('/api/me/notifications', {
        method: 'PUT',
        body: { sms: turningOn },
      });
      setNotify(result.notifications);
    } catch {
      setNotify(previous);
      toast.error("Couldn't save that change. Try again.");
      trackFailed('settings_save', 'error');
    } finally {
      setNotifySaving(false);
    }
  };

  // On → off on the two switches that carry the product's whole reason asks a
  // word first; everything else flips in place.
  const flip = (key: PrivacyKey): void => {
    if (!prefs || saving) return;
    if (prefs[key] && ASKS_FIRST[key]) { setAsking(key); return; }
    void save(key);
  };

  const save = (key: PrivacyKey): Promise<void> => savePatch(key, { [key]: !prefs?.[key] });

  // One PUT for one or more switches; optimistic, rolled back on failure.
  const savePatch = async (key: PrivacyKey, patch: Partial<PrivacyPrefs>): Promise<void> => {
    if (!prefs || saving) return;
    setSaving(key);
    setPrefs({ ...prefs, ...patch });
    try {
      const result = await apiFetch<{ ok: true; privacy: PrivacyPrefs }>('/api/me/privacy', {
        method: 'PUT',
        body: patch,
      });
      setPrefs({
        ...result.privacy,
        share: result.privacy.share !== false,
        background_experience: result.privacy.background_experience !== false,
      });
      if (key === 'background_experience' || key === 'share') void refreshSession();
    } catch {
      setPrefs(prefs);
      toast.error("Couldn't save that change. Try again.");
      trackFailed('settings_save', 'error');
    } finally {
      setSaving(null);
    }
  };

  const changeLanguage = async (next: string): Promise<void> => {
    if (!language || languageSaving || next === language.language) return;
    const previous = language.language;
    setLanguageSaving(true);
    setLanguage({ ...language, language: next });
    try {
      const result = await apiFetch<{ ok: true; language: string }>('/api/me/story-language', {
        method: 'PUT',
        body: { language: next },
      });
      setLanguage((current) => (current ? { ...current, language: result.language } : current));
    } catch {
      setLanguage((current) => (current ? { ...current, language: previous } : current));
      toast.error("Couldn't save that change. Try again.");
      trackFailed('settings_save', 'error');
    } finally {
      setLanguageSaving(false);
    }
  };

  // One About you row's change (2026-10-20 Riley). The answer is the stored truth, so the page takes the whole
  // profile back from it. ui.clicked settings_profile_<row> says set | cleared once it is saved — never the value,
  // which is the person's own and rides no analytics prop.
  const saveProfile = async (key: ProfileKey, value: ProfileValue): Promise<boolean> => {
    if (!profile || profileSaving) return false;
    if (value === DECLINED ? declinedRow(key) : profile[key] === value && !declinedRow(key)) return true;
    setProfileSaving(key);
    try {
      const result = await apiFetch<{ ok: true; profile: Profile }>('/api/me/profile', {
        method: 'PUT',
        body: { [key]: value },
      });
      setProfile(result.profile);
      track('ui.clicked', { name: PROFILE_LOG[key], state: value === null ? 'cleared' : value === DECLINED ? 'declined' : 'set' });
      return true;
    } catch (err) {
      trackFailed('settings_profile', apiFailureReason(err), { step: key });
      toast.error("Couldn't save that change. Try again.");
      return false;
    } finally {
      setProfileSaving(null);
    }
  };

  // A row answered "Prefer not to say" says so; an empty one offers Add.
  const declinedRow = (key: ProfileKey): boolean => Boolean(profile?.declined?.includes(key));
  const rowValue = (key: ProfileKey, text: string | number | null | undefined): string => (
    declinedRow(key) ? 'Prefer not to say' : text ? String(text) : 'Add'
  );

  const languageLabel = language
    ? (language.languages.find((l) => l.code === language.language)?.label || language.language)
    : 'English';

  // Which text row stands (2026-10-19 Riley). The switch until /api/me answers (it renders busy), and whenever
  // the consent is in hand; the START row when there is a line to text; the add-phone row when the account has
  // no number at all. A number with no line (the pool unreachable) keeps the busy switch rather than an action
  // that cannot work.
  const textState: 'switch' | 'start' | 'add_phone' = !notify || (notify.sms.in_window && !optedOut)
    ? 'switch'
    : notify.line ? 'start'
      : identities && !identities.phone && !identities.imessage?.handle ? 'add_phone' : 'switch';

  if (!import.meta.env.DEV && status !== 'authenticated') {
    return <div className="ember-page-quiet">One moment…</div>;
  }

  return (
    <div className="ember-container settings-page">
      <div className="settings-toolbar">
        <PageBack className="settings-back" returnTo={backAfterGoogle || undefined} />
        {/* Level 3, like the detail pages: Back walks up to Profile, Home is
            the one-tap exit (§3 Back-and-title contract). */}
        <div className="ember-toolbar-actions">
          <PageHome />
        </div>
      </div>
      <header className="settings-intro">
        <h1 className="settings-title">Settings</h1>
      </header>

      {/* Identity (symbol + line) is edited on the profile header itself
          (2026-09-01 拍板, retiring /settings/space): this page holds only
          what has no home on /me. */}

      {/* About you (2026-10-20 Riley): three things a person may tell Ember about themselves, each optional and
          asked for nowhere else. Rows in the Spoken language shape: the label alone on the left, the value (or
          Add) and a chevron on the right. Pronouns and Year of birth open the platform picker; What you do opens
          a one-line field. */}
      <section className="settings-section" aria-labelledby="settings-about">
        <h2 id="settings-about" className="settings-section-title">About you</h2>
        <p className="settings-section-note">Ember also picks these up when you mention them, so your Embers sound and look like you. You can change any of them here.</p>
        <ul className="settings-rows">
          <li className="settings-row">
            <div className="settings-row-text">
              <span className="settings-row-title">Pronouns</span>
            </div>
            <label className={`settings-lang${!profile || profileSaving ? ' is-disabled' : ''}`}>
              <span className="settings-lang-value" aria-hidden>
                {rowValue('pronouns', profile?.pronouns ? PRONOUN_LABELS[profile.pronouns] : null)}
              </span>
              <svg className="settings-lang-chevron" viewBox="0 0 10 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M1 1.25 5 5.25 9 1.25" />
              </svg>
              <select
                aria-label="Pronouns"
                value={declinedRow('pronouns') ? 'none' : profile?.pronouns || ''}
                disabled={!profile || profileSaving !== null}
                onChange={(e) => {
                  const v = e.target.value;
                  void saveProfile('pronouns', v === 'she' || v === 'he' || v === 'they' ? v : DECLINED);
                }}
              >
                <option value="" disabled hidden>Add</option>
                <option value="she">She</option>
                <option value="he">He</option>
                <option value="they">They</option>
                <option value="none">Prefer not to say</option>
              </select>
            </label>
          </li>
          <li className="settings-row">
            <div className="settings-row-text">
              <span className="settings-row-title">Year of birth</span>
            </div>
            <label className={`settings-lang${!profile || profileSaving ? ' is-disabled' : ''}`}>
              <span className="settings-lang-value" aria-hidden>{rowValue('birth_year', profile?.birth_year)}</span>
              <svg className="settings-lang-chevron" viewBox="0 0 10 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M1 1.25 5 5.25 9 1.25" />
              </svg>
              <select
                aria-label="Year of birth"
                value={declinedRow('birth_year') ? 'none' : profile?.birth_year != null ? String(profile.birth_year) : ''}
                disabled={!profile || profileSaving !== null}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  void saveProfile('birth_year', Number.isInteger(v) && v > 0 ? v : DECLINED);
                }}
              >
                <option value="" disabled hidden>Add</option>
                {birthYears().map((y) => <option key={y} value={String(y)}>{y}</option>)}
                <option value="none">Prefer not to say</option>
              </select>
            </label>
          </li>
          <li className="settings-row">
            <div className="settings-row-text">
              <span className="settings-row-title">What you do</span>
            </div>
            <button
              type="button"
              className="settings-lang"
              aria-label={`What you do: ${rowValue('occupation', profile?.occupation)}`}
              disabled={!profile || profileSaving !== null}
              onClick={() => setOccupationOpen(true)}
            >
              <span className="settings-lang-value is-truncated">{rowValue('occupation', profile?.occupation)}</span>
              <ForwardChevron />
            </button>
          </li>
          <li className="settings-row">
            <div className="settings-row-text">
              <span className="settings-row-title">Relationship</span>
            </div>
            <label className={`settings-lang${!profile || profileSaving ? ' is-disabled' : ''}`}>
              <span className="settings-lang-value" aria-hidden>
                {rowValue('relationship', profile?.relationship ? RELATIONSHIP_LABELS[profile.relationship] : null)}
              </span>
              <svg className="settings-lang-chevron" viewBox="0 0 10 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M1 1.25 5 5.25 9 1.25" />
              </svg>
              <select
                aria-label="Relationship"
                value={declinedRow('relationship') ? 'none' : profile?.relationship || ''}
                disabled={!profile || profileSaving !== null}
                onChange={(e) => {
                  const v = e.target.value;
                  void saveProfile('relationship', v in RELATIONSHIP_LABELS ? v : DECLINED);
                }}
              >
                <option value="" disabled hidden>Add</option>
                {(Object.keys(RELATIONSHIP_LABELS) as Relationship[]).map((r) => <option key={r} value={r}>{RELATIONSHIP_LABELS[r]}</option>)}
                <option value="none">Prefer not to say</option>
              </select>
            </label>
          </li>
          <li className="settings-row">
            <div className="settings-row-text">
              <span className="settings-row-title">Where you live</span>
            </div>
            <button
              type="button"
              className="settings-lang"
              aria-label={`Where you live: ${rowValue('location', profile?.location)}`}
              disabled={!profile || profileSaving !== null}
              onClick={() => setLocationOpen(true)}
            >
              <span className="settings-lang-value is-truncated">{rowValue('location', profile?.location)}</span>
              <ForwardChevron />
            </button>
          </li>
        </ul>
      </section>

      {/* Sharing your Embers (§4.9 rewritten, 2026-09-27 拍板): the
          account-wide switch — may Ember ask this person about an Ember of
          theirs that could help someone, and offer the ones they said yes
          to. Off: no asks, no offers; what people were already given stays
          until closed on the Ember's own page. The yes on one Ember is the
          per-Ember twin (its own page). Its words (the sub-line by state,
          the ? box, the off sheet) are one source for all three clients:
          src/shared/client-copy.js legal.share (2026-10-21 Riley). */}
      <section className="settings-section" aria-labelledby="settings-share">
        <h2 id="settings-share" className="settings-section-title">Another person reading your Ember</h2>
        <ul className="control-rows">
          <ControlRow
            title="Sharing your Embers"
            helpKey="share_global"
            help={SHARE_COPY.help.map((p) => <p key={p}>{p}</p>)}
            detail={(!prefs || prefs.share ? SHARE_COPY.on : SHARE_COPY.off)
              .map((line) => <span key={line} className="control-row-detail">{line}</span>)}
            control={(
              <Switch
                on={prefs ? prefs.share : true}
                label="Sharing your Embers"
                busy={!prefs || saving !== null}
                log="share_global_toggle"
                onFlip={() => flip('share')}
              />
            )}
          />
        </ul>
      </section>

      {/* Background experience (2026-09-12 data contract): the account-wide
          switch for the layer where eligible Embers, identifying details
          removed, inform what Ember tells other people at a light — the light
          pages' weekly entries and Ask this light (since 2026-09-24 拍板 the
          conversation itself carries no accounts). On by default; off stops
          future background use (eligibility is re-checked at every read). It
          never lets anyone read an Ember — that is the switch above. Its
          words (the sub-line by state, the ? box, the off sheet) are the
          lawyer's, one source for all three clients: src/shared/client-copy.js
          (2026-10-21 Riley), the apps read the same over /api/app/config. */}
      <section className="settings-section" aria-labelledby="settings-background">
        <h2 id="settings-background" className="settings-section-title">Help Ember understand similar situations</h2>
        <ul className="control-rows">
          <ControlRow
            title="Your experience informs Ember"
            helpKey="background_global"
            help={BACKGROUND_COPY.help.map((p) => <p key={p}>{p}</p>)}
            detail={(!prefs || prefs.background_experience ? BACKGROUND_COPY.on : BACKGROUND_COPY.off)
              .map((line) => <span key={line} className="control-row-detail">{line}</span>)}
            control={(
              <Switch
                on={prefs ? prefs.background_experience : true}
                label="Help Ember understand similar situations"
                busy={!prefs || saving !== null}
                log="background_global_toggle"
                onFlip={() => flip('background_experience')}
              />
            )}
          />
        </ul>
      </section>
      {/* Rooms (§4.21): the standing wish to be invited into a room around things this person lived (2026-09-25 →
          2026-10-26) is gone with the rooms by situation — rooms are put up and joined on the New friends page now. */}
      <section className="settings-section" aria-labelledby="settings-voice">
        <h2 id="settings-voice" className="settings-section-title">Voice calls</h2>
        <ul className="settings-rows">
          <li className="settings-row">
            <div className="settings-row-text">
              <span className="settings-row-title">Spoken language</span>
              <span className="settings-row-detail">
                Used for voice conversations only &mdash; a call in the browser, or a call
                from your own phone. Your Ember itself is still written
                in English.
              </span>
            </div>
            {/* Same shape as the /collect language row: the native select is
                invisible and covers the visible value + chevron, so the whole
                control opens the platform picker. */}
            <label
              className={`settings-lang${!language || languageSaving ? ' is-disabled' : ''}`}
              data-log="settings_language"
              data-log-state={language?.language || ''}
            >
              <span className="settings-lang-value" aria-hidden>{languageLabel}</span>
              <svg className="settings-lang-chevron" viewBox="0 0 10 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M1 1.25 5 5.25 9 1.25" />
              </svg>
              <select
                aria-label="Spoken language for voice calls"
                value={language?.language || 'en'}
                disabled={!language || languageSaving}
                onChange={(e) => { void changeLanguage(e.target.value); }}
              >
                {(language?.languages || [{ code: 'en', label: 'English' }]).map(({ code, label }) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            </label>
          </li>
        </ul>
      </section>

      <section className="settings-section" aria-labelledby="settings-notifications">
        <h2 id="settings-notifications" className="settings-section-title">Notifications</h2>
        <ul className="settings-rows">
          {/* The text row by state (2026-10-19 Riley), never a switch that can be tapped and then fail:
              no number → add one; a number but no consent text yet → the text that gives it; the
              consent in hand → the switch. */}
          {textState !== 'switch' ? (
            // The two states before the switch are one row like Spoken language (2026-10-19 Riley): the
            // title on the left, a value + chevron on the right — Add phone, or Turn on (Waiting… once
            // the text is handed to Messages) — not an action line of its own.
            <li className="settings-row">
              <div className="settings-row-text">
                <span className="settings-row-title">Texts from Ember</span>
                {notifyPending && notifyBody && notify?.line ? (
                  <span className="settings-row-detail">From your phone, text &ldquo;{notifyBody}&rdquo; to {formatPhone(notify.line)}</span>
                ) : null}
              </div>
              {textState === 'add_phone' ? (
                <button
                  type="button"
                  className="settings-lang"
                  data-log="settings_notify_add_phone"
                  onClick={() => setPhoneOpen(true)}
                >
                  <span className="settings-lang-value">Add phone</span>
                  <ForwardChevron />
                </button>
              ) : (
                <button
                  type="button"
                  className="settings-lang"
                  data-log="settings_notify_start"
                  data-log-state="row"
                  disabled={notifySaving}
                  onClick={() => { void startConsentText(); }}
                >
                  {notifyPending ? (
                    // Seen on this conditionally-mounted node, not the row (see the switch row below).
                    <span className="settings-lang-value" data-log-seen="settings_notify_pending">Waiting&hellip;</span>
                  ) : (
                    <span className="settings-lang-value">Turn on</span>
                  )}
                  <ForwardChevron />
                </button>
              )}
            </li>
          ) : (
            <li className="settings-row">
              <div className="settings-row-text">
                <span className="settings-row-title">
                  Ember reaching out
                  <HelpTip which="notifications" label="Ember reaching out">
                    <p>With this on, Ember may text you first when it has something to tell you — someone it thinks you’d get along with, news from a room you’re in, a note someone left on your Ember, the evening your Ember is written, your weekly letter, or after a few quiet days, to see how you are. Never ads; a few texts, only about your own things.</p>
                    <p>Turn it off and Ember waits for you to write first; a reminder you asked Ember for still comes. Replying STOP to any text stops them all, reminders too.</p>
                  </HelpTip>
                </span>
                {/* State, not explanation: when it lapses, or what is being
                    waited for. Nothing when the switch is simply off. */}
                {optedOut ? (
                  <span className="settings-row-detail">Stopped &mdash; text Ember to turn it back on</span>
                ) : notifyPending ? (
                  // The seen attribute lives on this conditionally-MOUNTED node,
                  // not on the always-present row: the observer only watches
                  // childList mutations, so an attribute added to an existing
                  // element is never seen (the row-level form was structurally
                  // dead — 2026-08-31 audit).
                  <span className="settings-row-detail" data-log-seen="settings_notify_pending">
                    {notifyBody && notify?.line ? <>From your phone, text &ldquo;{notifyBody}&rdquo; to {formatPhone(notify.line)}</> : <>Waiting for your text&hellip;</>}
                  </span>
                ) : notify?.sms.active && notify.sms.expires_at ? (
                  <span className="settings-row-detail">On until {fullDate(notify.sms.expires_at)}</span>
                ) : notify && !notify.sms.opted_out && !notify.sms.in_window && notify.sms.last_inbound_at ? (
                  <span className="settings-row-detail">Paused &mdash; text {notify.keyword} to turn back on</span>
                ) : (
                  <span className="settings-row-detail">
                    {notify && notify.sms.opted_out
                      ? 'Ember won\u2019t reach out first. You can always text Ember.'
                      : 'Ember may text you first when it has something to tell you \u2014 someone you\u2019d get along with, news from a room, a note someone left, the evening your Ember is written, or after a few quiet days.'}
                  </span>
                )}
              </div>
              <Switch
                on={Boolean(notify?.sms.active && !optedOut)}
                label="Ember reaching out"
                busy={!notify || notifySaving || notifyPending || optedOut || (!notify.sms.in_window && !notify.line)}
                pending={notifyPending}
                log="settings_notify_toggle"
                onFlip={() => { void flipNotify(); }}
              />
            </li>
          )}
        </ul>
      </section>

      <section className="settings-section" aria-labelledby="settings-data-controls">
        <h2 id="settings-data-controls" className="settings-section-title">Data controls</h2>
        <ul className="control-rows">
          {DATA_CONTROLS.map(({ key, title, detail, help }) => (
            <ControlRow
              key={key}
              title={title}
              helpKey={key}
              help={help.map((p) => <p key={p}>{p}</p>)}
              detail={<span className="control-row-detail">{detail}</span>}
              control={(
                <DataSwitch
                  on={prefs ? prefs[key] : true}
                  label={title}
                  busy={!prefs || saving !== null}
                  logState={`${key}:${prefs && prefs[key] ? 'off' : 'on'}`}
                  onFlip={() => flip(key)}
                />
              )}
            />
          ))}
        </ul>
      </section>

      <section className="settings-section" aria-labelledby="settings-account">
        <h2 id="settings-account" className="settings-section-title">Account</h2>
        <ul className="settings-rows">
          {/* ── 绑定行(2026-09-09,App Settings 同款):账号可以住在号上;Google
                首登的账号还没有号 → Connect 走 add-phone 两步。绑上只亮脱敏号,
                不给解——手机号永不可解。 ── */}
          {identities?.imessage?.handle ? (
            <li className="settings-row">
              <div className="settings-row-text">
                <span className="settings-row-title">iMessage</span>
                {/* 这种账号的根身份:只亮,不给动——它就是这个账号本身 */}
                <span className="settings-row-detail">{identities.imessage.handle}</span>
              </div>
            </li>
          ) : null}
          {identities ? (
            <li className="settings-row">
              <div className="settings-row-text">
                <span className="settings-row-title">
                  Phone
                  {phoneHint && !identities.phone ? <span className="settings-hint-dot" aria-hidden /> : null}
                </span>
                {identities.phone ? (
                  <span className="settings-row-detail">{identities.phone}</span>
                ) : (
                  <span className="settings-row-detail">
                    Ember can only reach out &mdash; your Ember written, a letter, a note after a few quiet days &mdash; if it has your number.
                  </span>
                )}
              </div>
              {!identities.phone ? (
                <button
                  type="button"
                  className="settings-quiet-action"
                  data-log="account_add_phone_open"
                  onClick={() => setPhoneOpen(true)}
                >
                  Connect
                </button>
              ) : null}
            </li>
          ) : null}
          {identities && googleRowReady ? (
            <li className="settings-row">
              <div className="settings-row-text">
                {/* 品牌图标在前、动作在右(2026-09-09 走查:右边一颗孤零零的 G
                    分不清是已绑还是没绑)——绑定状态只由 Connect/Disconnect 说 */}
                <span className="settings-row-title settings-row-title-branded"><GoogleG size={20} />Google</span>
                {identities.google?.email ? (
                  <span className="settings-row-detail">{identities.google.email}</span>
                ) : null}
              </div>
              {identities.google ? (
                <button
                  type="button"
                  className="settings-quiet-action"
                  data-log="account_google_disconnect_open"
                  onClick={() => {
                    // Google 是唯一一扇门时不给拆(服务端同有 last_identity 闸);
                    // 邮箱 iMessage 也是一扇门,别把它算漏
                    if (!identities.phone && !identities.apple && !identities.imessage) {
                      toast.error('Connect a phone number first — Google is the only way into this account.');
                      return;
                    }
                    setUnlinkOpen(true);
                  }}
                >
                  Disconnect
                </button>
              ) : (
                // 授权码流的 link 模式:整页去 Google,身份挂到当前账号,回跳
                // 落回本页(hash 报结果)
                <button
                  type="button"
                  className="settings-quiet-action"
                  data-log="account_google_connect"
                  onClick={() => {
                    const back = backAfterGoogle || resolveBack(null, '/').path;
                    window.location.assign(googleLinkHref(`/settings?back=${encodeURIComponent(back)}`));
                  }}
                >
                  Connect
                </button>
              )}
            </li>
          ) : null}
          <li className="settings-row">
            <div className="settings-row-text">
              <span className="settings-row-title">Delete account</span>
              <span className="settings-row-detail">
                Schedule your account and every Ember you&apos;ve told for deletion.
              </span>
            </div>
            <button
              type="button"
              className="ember-btn ember-btn-danger settings-delete-btn"
              data-log="account_delete_open"
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </button>
          </li>
        </ul>
      </section>

      {asking && ASKS_FIRST[asking] ? (
        <PrivacyOffSheet
          which={ASKS_FIRST[asking] as PrivacyOffKey}
          onKeep={() => setAsking(null)}
          onTurnOff={() => {
            const key = asking; setAsking(null);
            void save(key);
          }}
        />
      ) : null}
      {deleteOpen ? <DeleteAccountModal onClose={() => setDeleteOpen(false)} /> : null}
      {phoneOpen ? (
        <PhoneConnectModal
          onClose={() => setPhoneOpen(false)}
          onBound={() => {
            clearPhoneHint(); setPhoneHint(false); void loadIdentities();
            // The text row reads the number's line from /api/me: without this reload it kept saying no_line until the page
            // was reloaded (2026-10-19; the App re-reads it since 10-16). With a line and no consent yet, the next step —
            // the consent text — opens straight away (2026-10-19 Riley): adding the number alone never turned texts on.
            apiFetch<MeResponse>('/api/me').then((me) => {
              if (!me.notifications) return;
              setNotify(me.notifications);
              setOptedOut(Boolean(me.messaging_opt_out?.opted_out));
              if (me.notifications.line && !me.notifications.sms.in_window) setTextsPromptOpen(true);
            }).catch(() => { /* next load */ });
          }}
        />
      ) : null}
      {occupationOpen && profile ? (
        <OccupationModal
          stored={profile.occupation}
          busy={profileSaving === 'occupation'}
          onSave={async (value) => { if (await saveProfile('occupation', value)) setOccupationOpen(false); }}
          onClose={() => setOccupationOpen(false)}
        />
      ) : null}
      {locationOpen && profile ? (
        <LocationModal
          stored={profile.location || null}
          busy={profileSaving === 'location'}
          onSave={async (value) => { if (await saveProfile('location', value)) setLocationOpen(false); }}
          onClose={() => setLocationOpen(false)}
        />
      ) : null}
      {textsPromptOpen ? (
        <Modal onClose={() => setTextsPromptOpen(false)} labelledBy="texts-prompt-title" className="settings-bind-modal">
          <h2 id="texts-prompt-title" className="settings-bind-title" data-log-seen="settings_notify_prompt">Get texts from Ember</h2>
          <p className="settings-bind-body">Send Ember a text to turn on texts.</p>
          <div className="settings-bind-actions">
            <button
              type="button"
              className="ember-btn ember-btn-ghost"
              data-log="settings_notify_later"
              onClick={() => setTextsPromptOpen(false)}
            >
              Not now
            </button>
            <button
              type="button"
              className="ember-btn ember-btn-primary"
              data-log="settings_notify_start"
              data-log-state="after_add"
              disabled={notifySaving}
              onClick={() => { setTextsPromptOpen(false); void startConsentText(); }}
            >
              Text Ember
            </button>
          </div>
        </Modal>
      ) : null}
      {unlinkOpen ? (
        <Modal onClose={() => setUnlinkOpen(false)} labelledBy="unlink-google-title" className="settings-bind-modal">
          <h2 id="unlink-google-title" className="settings-bind-title">Disconnect Google?</h2>
          <p className="settings-bind-body">
            You can still sign in with your phone number &mdash; that&rsquo;s what your account lives on.
          </p>
          <div className="settings-bind-actions">
            <button type="button" className="ember-btn ember-btn-ghost" onClick={() => setUnlinkOpen(false)}>Cancel</button>
            <button
              type="button"
              className="ember-btn ember-btn-primary"
              data-log="account_google_disconnect"
              onClick={() => {
                setUnlinkOpen(false);
                void (async () => {
                  try {
                    await apiFetch('/api/app/auth/unlink/google', { method: 'POST' });
                    await loadIdentities();
                  } catch (e) {
                    const code = e instanceof ApiError ? String((e.body as { error?: string })?.error || '') : '';
                    trackFailed('account_unlink', code || 'error', { provider: 'google' });
                    toast.error(code === 'last_identity'
                      ? 'Connect a phone number first — Google is the only way into this account.'
                      : "That didn't go through. Try again.");
                  }
                })();
              }}
            >
              Disconnect
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

/**
 * Phone 绑定的两步弹框(2026-09-09,App 的 add-phone 弹框网页同款):第一步输
 * 整号(带国家码)发码,第二步验 6 位码。多出网页独有的第三幕——空号嫁接:
 * 号在别人账号上而当前账号是空的时,verify 的 409 会附一张 graft ticket,这里
 * 问一句「切到那个账号?」,点头即把 Google 身份搬过去、会话换发、整页重载。
 */
/**
 * The data-controls switch keeps its historical `data-log-state` grammar
 * (`<key>:on|off`, settings_data_toggle) so the rows before and after
 * 2026-09-12 read as one series.
 */
/** The language row's chevron turned to point forward (2026-10-19 Riley): the text row's Add phone / Turn on
    goes somewhere rather than opening a picker. */
function ForwardChevron(): ReactElement {
  return (
    <svg className="settings-lang-chevron is-forward" viewBox="0 0 10 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1 1.25 5 5.25 9 1.25" />
    </svg>
  );
}

/** What you do (2026-10-20 Riley): one line, at most 60 characters. Save keeps it; Clear (when one is kept) takes it away. */
function OccupationModal({ stored, busy, onSave, onClose }: {
  stored: string | null; busy: boolean; onSave: (value: string | null) => Promise<void>; onClose: () => void;
}): ReactElement {
  const [text, setText] = useState(stored || '');
  // One line, as the server keeps it (ember/about-you.js cleanOccupation): a pasted newline is a space.
  const value = text.replace(/\s+/g, ' ').trim();
  const canSave = !busy && value.length > 0 && value !== (stored || '');
  return (
    <Modal onClose={onClose} labelledBy="occupation-title" className="settings-bind-modal" dismissible={!busy}>
      <form onSubmit={(e) => { e.preventDefault(); if (canSave) void onSave(value); }}>
        <h2 id="occupation-title" className="settings-bind-title">What you do</h2>
        <input
          className="settings-bind-input"
          type="text"
          aria-labelledby="occupation-title"
          autoComplete="organization-title"
          enterKeyHint="done"
          maxLength={OCCUPATION_MAX}
          value={text}
          onChange={(e) => setText(e.target.value.replace(/[\r\n]+/g, ' '))}
        />
        <div className="settings-bind-actions">
          <button type="button" className="ember-btn ember-btn-ghost" disabled={busy} onClick={() => { void onSave(DECLINED); }}>
            Prefer not to say
          </button>
          {stored ? (
            <button type="button" className="ember-btn ember-btn-ghost" disabled={busy} onClick={() => { void onSave(null); }}>
              Clear
            </button>
          ) : (
            <button type="button" className="ember-btn ember-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          )}
          <button type="submit" className="ember-btn ember-btn-primary" disabled={!canSave}>Save</button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Where you live (2026-10-20): type a town, a state or a country and pick it from the list (GET /api/places — searched
 * on Ember's own list of places, so what they type goes nowhere else). A pick also sets their clock. Clear takes it
 * away; Prefer not to say keeps Ember from picking it up from what they say.
 */
function LocationModal({ stored, busy, onSave, onClose }: {
  stored: string | null; busy: boolean; onSave: (value: string | null) => Promise<void>; onClose: () => void;
}): ReactElement {
  const [text, setText] = useState('');
  const [found, setFound] = useState<{ q: string; places: Array<{ id: string; label: string }> }>({ q: '', places: [] });
  const q = text.trim();
  useEffect(() => {
    if (q.length < 2) return undefined;
    let live = true;
    const timer = window.setTimeout(() => {
      apiFetch<{ places: Array<{ id: string; label: string }> }>(`/api/places?q=${encodeURIComponent(q)}`)
        .then((r) => { if (live) setFound({ q, places: Array.isArray(r.places) ? r.places : [] }); })
        .catch(() => { if (live) setFound({ q, places: [] }); });
    }, 150);
    return () => { live = false; window.clearTimeout(timer); };
  }, [q]);
  const places = q.length >= 2 && found.q === q ? found.places : [];
  return (
    <Modal onClose={onClose} labelledBy="location-title" className="settings-bind-modal" dismissible={!busy}>
      <h2 id="location-title" className="settings-bind-title">Where you live</h2>
      {stored ? <p className="settings-bind-body">Now: {stored}</p> : null}
      <input
        className="settings-bind-input"
        type="text"
        aria-labelledby="location-title"
        placeholder="Your town, city or state"
        autoComplete="off"
        enterKeyHint="search"
        maxLength={80}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {places.length ? (
        <ul className="settings-place-list">
          {places.map((p) => (
            <li key={p.id}>
              <button type="button" className="settings-place-option" disabled={busy} onClick={() => { void onSave(p.id); }}>
                {p.label}
              </button>
            </li>
          ))}
        </ul>
      ) : q.length >= 2 && found.q === q ? (
        <p className="settings-bind-body">Nothing by that name yet. Try a nearby town or your state.</p>
      ) : null}
      <div className="settings-bind-actions">
        <button type="button" className="ember-btn ember-btn-ghost" disabled={busy} onClick={() => { void onSave(DECLINED); }}>
          Prefer not to say
        </button>
        {stored ? (
          <button type="button" className="ember-btn ember-btn-ghost" disabled={busy} onClick={() => { void onSave(null); }}>
            Clear
          </button>
        ) : (
          <button type="button" className="ember-btn ember-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
        )}
      </div>
      <p className="settings-place-credit">Place names from GeoNames</p>
    </Modal>
  );
}

function DataSwitch({ on, label, busy, logState, onFlip }: {
  on: boolean; label: string; busy: boolean; logState: string; onFlip: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={`ember-switch${on ? ' is-on' : ''}`}
      disabled={busy}
      data-log="settings_data_toggle"
      data-log-state={logState}
      onClick={onFlip}
    >
      <span className="ember-switch-knob" aria-hidden />
    </button>
  );
}

function PhoneConnectModal({ onClose, onBound }: { onClose: () => void; onBound: () => void }): ReactElement {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState('');
  const [graftTicket, setGraftTicket] = useState('');
  const [graftPhone, setGraftPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const digits = (s: string): string => s.replace(/\D/g, '');
  const errorCode = (e: unknown): string => (e instanceof ApiError ? String((e.body as { error?: string })?.error || '') : '');
  // The add-phone steps under the sign-in door's names (2026-10-19 Riley; the apps' phone door since 10-18):
  // ui.failed phone_code_send / phone_code_verify with step settings_add, reason = the API error code (else
  // http_<status> | network_error), error_detail = the status and code as they came back (apiFailureReason).
  const failed = (name: 'phone_code_send' | 'phone_code_verify', e: unknown): void => {
    const codeName = errorCode(e);
    const reason = codeName || (e instanceof ApiError ? `http_${e.status}` : 'network_error');
    const detail = e instanceof ApiError ? `http_${e.status}${codeName ? `:${codeName}` : ''}` : apiFailureReason(e);
    trackFailed(name, reason, { step: 'settings_add', error_detail: detail.slice(0, 120) });
  };

  const sendCode = async (): Promise<void> => {
    if (busy || digits(phone).length < 5) return;
    setBusy(true); setError('');
    try {
      const r = await apiFetch<{ challenge_id: string }>('/api/app/auth/add-phone/start', {
        method: 'POST', body: { phone: `+${digits(phone)}` },
      });
      setChallenge(r.challenge_id);
      // The code is on its way — the sign-in door's login.code_sent, told apart by step (2026-10-19 Riley)
      track('login.code_sent', { arrival: 'settings', step: 'settings_add', channel: 'server' });
    } catch (e) {
      const codeName = errorCode(e);
      failed('phone_code_send', e);
      setError(codeName === 'invalid_phone'
        ? 'That doesn’t look like a phone number — include the country code, like +1.'
        : codeName === 'resend_too_soon' ? 'One moment — a code was just sent.'
          : codeName === 'too_many_requests' ? 'Too many tries for this number. Wait a while and try again.'
            : 'Couldn’t text that number right now. Try again.');
    } finally { setBusy(false); }
  };

  const verify = async (): Promise<void> => {
    if (busy || digits(code).length !== 6) return;
    setBusy(true); setError('');
    try {
      await apiFetch('/api/app/auth/add-phone/verify', {
        method: 'POST', body: { challenge_id: challenge, code: digits(code) },
      });
      toast.success('Phone connected');
      onBound();
      onClose();
    } catch (e) {
      const codeName = errorCode(e);
      const body = e instanceof ApiError ? (e.body as { graft_ticket?: string; phone?: string }) : {};
      if (codeName === 'phone_already_linked' && body?.graft_ticket) {
        // 空号嫁接的那一问——切换账号必须本人点头,静默换人太吓人
        setGraftTicket(body.graft_ticket);
        setGraftPhone(body.phone || '');
        return;
      }
      failed('phone_code_verify', e);
      if (codeName === 'phone_already_linked') setError('That number is already on another Ember account.');
      else if (codeName === 'code_incorrect') setError('That code didn’t match. Try again.');
      else if (codeName === 'code_expired' || codeName === 'too_many_attempts') {
        setChallenge(''); setCode('');
        setError('That code expired. Send a new one.');
      } else setError('That didn’t go through. Try again.');
    } finally { setBusy(false); }
  };

  const graft = async (): Promise<void> => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await apiFetch('/api/app/auth/add-phone/graft', { method: 'POST', body: { graft_ticket: graftTicket } });
      // 会话已经换发到号主:这一页的所有读数都属于旧账号,整页重来
      window.location.reload();
    } catch (e) {
      const codeName = errorCode(e);
      trackFailed('account_add_phone', codeName || 'error', { step: 'graft' });
      if (codeName === 'ticket_expired') {
        setGraftTicket(''); setChallenge(''); setCode('');
        setError('That took too long — send a new code.');
      } else if (codeName === 'identity_conflict') {
        setGraftTicket('');
        setError('That account already has a different Google connected.');
      } else setError('That didn’t go through. Try again.');
      setBusy(false);
    }
  };

  const step: 'phone' | 'code' | 'graft' = graftTicket ? 'graft' : challenge ? 'code' : 'phone';
  return (
    <Modal onClose={onClose} labelledBy="add-phone-title" className="settings-bind-modal" dismissible={!busy}>
      <h2 id="add-phone-title" className="settings-bind-title">
        {step === 'graft' ? 'That number already has an Ember account' : 'Add your phone number'}
      </h2>
      {step === 'graft' ? (
        <p className="settings-bind-body">
          {graftPhone ? `${graftPhone} is` : 'It’s'} the number an existing Ember account lives on.
          This Google sign-in can move onto that account &mdash; this new, empty one goes away,
          and you continue as the account your number knows.
        </p>
      ) : (
        <p className="settings-bind-body">
          {step === 'code'
            ? `Enter the 6-digit code we texted to +${digits(phone)}.`
            : 'We’ll text a code to confirm it’s yours.'}
        </p>
      )}
      {step === 'phone' ? (
        <input
          className="settings-bind-input"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+1 415 555 0100"
          value={phone}
          onChange={(e) => { setPhone(e.target.value); setError(''); }}
        />
      ) : null}
      {step === 'code' ? (
        <input
          className="settings-bind-input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="6-digit code"
          value={code}
          onChange={(e) => { setCode(e.target.value); setError(''); }}
        />
      ) : null}
      {error ? <p className="settings-bind-error">{error}</p> : null}
      <div className="settings-bind-actions">
        <button type="button" className="ember-btn ember-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        {step === 'phone' ? (
          <button
            type="button"
            className="ember-btn ember-btn-primary"
            data-log="account_add_phone_send"
            disabled={busy || digits(phone).length < 5}
            onClick={() => { void sendCode(); }}
          >
            Send code
          </button>
        ) : step === 'code' ? (
          <button
            type="button"
            className="ember-btn ember-btn-primary"
            data-log="account_add_phone_verify"
            disabled={busy || digits(code).length !== 6}
            onClick={() => { void verify(); }}
          >
            Verify
          </button>
        ) : (
          <button
            type="button"
            className="ember-btn ember-btn-primary"
            data-log="account_graft_confirm"
            disabled={busy}
            onClick={() => { void graft(); }}
          >
            Use that account
          </button>
        )}
      </div>
    </Modal>
  );
}
